import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import {
  useFlagAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/shadcn/components/ui/input";
import { XIcon } from "lucide-react";
import { readFile } from "@/helpers/cropImage";
import { AccessibilityTag, AddPhotoImageData } from "common/types";
import Zoom from "react-medium-image-zoom";
import { bucketUrlPrefix } from "common/config";
import { useCustomToast } from "@/hooks/useCustomToast";
import WideButton from "@/components/common/WideButton";
import { Storage } from "common/storage";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";
import AddClaim from "../../components/VenueDetailBasic/Feature/AddClaim";
import Checkbox from "@/components/common/Checkbox";
import { trackEvent } from "common/analytics";

type FlagVenueAccessibilityProps = {
  venueId: string;
  mainTag: AccessibilityTag;
  venueName: string;
  catName: string;
  isOpen: boolean;
  onClose: () => void;
  venueDetails: any;
};

function FlagVenueAccessibility({
  venueId,
  mainTag,
  catName,
  venueName,
  isOpen,
  onClose,
  venueDetails,
}: FlagVenueAccessibilityProps) {
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();

  const showToast = useCustomToast();

  const [note, setNote] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const isZoomedIn = useZoomDetection();

  const { mutate, error, isPending, data } = useFlagAccessibility();
  const [myBusinessSelected, setMyBusinessSelected] = useState(false);
  const [flagVenueAccessibilityId, setFlagVenueAccessibilityId] = useState<
    string | null
  >(null);

  const handleMyBusinessClick = (checked: boolean) => {
    setMyBusinessSelected(checked);
  };

  const [addClaimOpenModal, setAddClaimOpenModal] = useState(false);

  useEffect(() => {
    if (error) {
      console.log("useFlagPhoto", error);

      showToast(error.message, "error");
    }
  }, [error, isDark]);

  useEffect(() => {
    if (data) {
      console.log("venueDetails", venueDetails);
      console.log("useFlagPhoto", data);
      // space_flagged — the basic-venue accessibility flag success. (The complex path already
      // fires this from FlagSpaceAccessibility; this modal previously fired nothing.) Basic
      // venues have no discrete space/header, so those dims are "". Fire before the state resets.
      trackEvent("space_flagged", {
        brand_id: venueDetails?.brandId ?? "",
        brand_name: venueDetails?.brandName ?? "",
        business_id:
          venueDetails?.business?.id != null
            ? String(venueDetails.business.id)
            : String(venueId ?? ""),
        photos_count: attachedImages.length,
        place_header_name: "",
        place_name: venueDetails?.name ?? "",
        place_space_name: "",
      });
      showToast(data.message, "success");
      setFlagVenueAccessibilityId(data.data.id);
      setAttachedImages([]);
      setNote("");
      onClose();

      console.log(
        "flagVenueAccessibilityId",
        flagVenueAccessibilityId,
        myBusinessSelected,
        data.data.id,
      );

      if (myBusinessSelected) {
        setTimeout(() => {
          console.log("open claim modal");
          setAddClaimOpenModal(true);
        }, 2000);
      }
    }
  }, [data, isDark]);

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const submit = () => {
    if (note.length < 5) {
      noteRef.current?.focus();
      setErrorMessage("Note must be at least 5 characters long");
      return;
    }
    if (attachedImages.length === 0) {
      setErrorMessage("Please add at least one image");
      return;
    }
    setErrorMessage(null);

    mutate({
      businessId: venueId,
      categoryId: mainTag?.categoryId?.toString(),
      typeQuestionId: mainTag?.questionId?.toString(),
      typeQuestionTagId: mainTag?.tagId?.toString(),
      accessToken: Storage.getCurrentUserToken(),
      notes: note,
      images: attachedImages,
    });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  const handleAddClaimModalClose = (isSuccess?: boolean) => {
    // Dismissed without submitting — claim_business_form_closed. A submit passes
    // isSuccess (fires business_claimed from AddClaim), so skip it here.
    if (isSuccess === undefined) {
      trackEvent("claim_business_form_closed", {
        brand_id: venueDetails?.brandId ?? "",
        brand_name: venueDetails?.brandName ?? "",
        business_id: venueDetails?.business?.id?.toString() ?? "",
        place_name: venueDetails?.name ?? "",
      });
    }
    setAddClaimOpenModal(false);
  };

  return (
    <>
      <Modal
        focusTrapped
        center
        blockScroll={true}
        animationDuration={0}
        open={isOpen}
        onClose={() => onClose()}
        classNames={{
          modal:
            "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
          closeButton: "hidden",
        }}
      >
        {isPending && <Loader />}
        <section
          style={modalStyle}
          className={cn(modalClassName)}
          aria-label={i18n.get("flagAccessibility")}
          ref={firstFocusRef}
        >
          <header
            className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
          >
            <StandardButton
              onClick={() => {
                setAttachedImages([]);
                setNote("");
                onClose();
              }}
              variant="closeModal"
              icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
              aria-label="Close modal"
              tabIndex={0}
              className="flex-shrink-0"
            />
            <div className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4">
              {i18n.get("flagAccessibility")}
            </div>
            <div className="flex-shrink-0 w-10"></div>{" "}
            {/* Spacer to balance the layout */}
          </header>

          <div
            aria-label="Modal Content"
            className="flex-grow overflow-y-auto py-3 px-3"
          >
            <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
              <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
                <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                  <div className="self-stretch !text-fg-brand-subtle label-s-medium">
                    {venueName}
                  </div>
                  <h1 className="self-stretch text-fg-neutral-strong title-s-semibold">
                    {i18n.get("helpUsCatchEveryError")}
                  </h1>
                </div>
                <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold pt-1">
                  {i18n.get("haveYouSpottedSomethingWrong")}
                </div>

                <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>
              </div>



              <div className="self-stretch rounded-lg justify-start items-center gap-8 inline-flex">
                {mainTag?.tag_image && (
                  <div className="w-[24px] h-[24px] relative">
                    <img
                      src={bucketUrlPrefix + mainTag.tag_image}
                      alt={`${catName} icon`}
                      aria-hidden="true"
                      className="w-[24px] h-[24px] mr-2 filter"
                    />
                  </div>
                )}
                <div className="h-14 flex-col justify-center items-start gap-1 inline-flex">
                  <div className="w-full title-s-semibold text-fg-neutral-strong">
                    {catName}
                  </div>
                  <div className="label-m-medium">{mainTag?.tagName}</div>
                </div>
              </div>

              <div className="md:hidden border w-full border-border-token-brand-minimal mt-1 h-[1px]"></div>

              <div className="w-full flex-col justify-center items-start gap-1 flex">
                <div className="w-full">
                  <label htmlFor="review" className="sr-only">
                    {i18n.get("tellUsWhichTagsAreIncorrect")}
                  </label>
                  <Textarea
                    id="review"
                    placeholder={i18n.get("tellUsWhichTagsAreIncorrect")}
                    className="scrollbar-thin h-28 "
                    value={note}
                    aria-describedby="charCount"
                    onChange={(e) => {
                      setErrorMessage(null);
                      setNote(e.target.value);
                    }}
                    maxLength={500}
                    ref={noteRef}
                    error={(note.length > 0 && note.length < 5) || Boolean(errorMessage)}
                  />
                  <div
                    id="charCount"
                    className={`label-s-medium mt-3 ${
                      note.length > 0 && note.length < 5
                        ? "text-fg-error-subtle"
                        : "text-fg-neutral-subtle"
                    }`}
                  >
                    {note.length > 0 && note.length < 5
                      ? i18n.get("pleaseEnterAtLeast5Characters")
                      : `${note.length}/500 characters`}
                  </div>

                  {errorMessage && !(note.length > 0 && note.length < 5) && (
                    <div className="text-fg-error-subtle" role="alert">
                      {errorMessage}
                    </div>
                  )}
                </div>
              </div>

              <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
                <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>
                <div className="self-stretch min-w-11 flex flex-col justify-center items-center">
                  <div className="self-stretch justify-center title-s-semibold text-fg-neutral-strong">
                    {i18n.get("areYouTheBusinessOwner")}
                  </div>
                </div>
                <div className="self-stretch min-w-11 inline-flex justify-center items-center gap-4 flex-wrap content-center">
                  <div className="flex-1 min-h-6 inline-flex flex-col justify-center items-start">
                    <div className="self-stretch justify-center">
                      <Checkbox
                        className=""
                        key="myBusiness"
                        label={i18n.get("yesThisIsMyBusiness")}
                        checked={myBusinessSelected}
                        onCheck={(value) => handleMyBusinessClick(value)}
                        aria-label={`Select my business, ${myBusinessSelected ? "currently selected" : "currently not selected"}`}
                      />
                    </div>
                  </div>
                </div>


              </div>

              <div className="w-full flex-col justify-center items-start gap-1 flex">
                <div className="grid gap-4 w-full">
                  {attachedImages.map((image, index) => (
                    <div key={index} className="grid gap-2">
                      <div className="relative w-full h-auto" tabIndex={0}>
                        <Zoom>
                          <img
                            src={image.imageUrl}
                            alt={`Attached image ${index + 1}`}
                            className="w-full h-[300px] object-cover rounded-[10px]"
                          />
                        </Zoom>
                        <StandardButton
                          onClick={() => {
                            setAttachedImages(
                              attachedImages.filter(
                                (_, imgIndex) => imgIndex !== index,
                              ),
                            );
                          }}
                          variant="removeImage"
                          position="absolute"
                          className="top-2 right-3 m-2"
                          imageSrc="/images/Close.svg"
                          imageAlt="Remove image"
                          imageContainer={true}
                          imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                          aria-label={`Remove attached image ${index + 1}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <footer
            className={`py-3 min-h-14 flex items-center justify-center text-center px-6 bg-bg-brand-secondary-default z-1000 ${!isZoomedIn ? "sticky bottom-0" : "relative"}`}
          >
            <div className="flex-col justify-end items-center gap-4 flex md:w-full">
              <div className="w-full justify-start items-center gap-4 inline-flex">
                <label
                  htmlFor="picture"
                  className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                  aria-label="Add photos"
                >
                  <div className="text-center text-fg-neutral-strong label-l-bold w-full">
                    {i18n.get("addPhotos")}
                  </div>
                  <Input
                    id="picture"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onFileChange}
                  />
                </label>
              </div>

              <div className="w-full justify-center items-center inline-flex">
                <WideButton
                  className="grow"
                  disabled={note.length < 5}
                  onClick={() => {
                    submit();
                  }}
                  aria-label="Submit flag"
                >
                  {i18n.get("submitFlag")}
                </WideButton>
              </div>
            </div>
          </footer>
        </section>
      </Modal>

      {addClaimOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addClaimOpenModal}
          onClose={() => handleAddClaimModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <AddClaim
            venueDetailsResponse={{ details: venueDetails } as any}
            onClose={handleAddClaimModalClose}
            onInfoClick={() => { }}
            flagVenueAccessibilityId={flagVenueAccessibilityId}
          />
        </Modal>
      )}
    </>
  );
}

export default FlagVenueAccessibility;
